import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, Globe, MapPin, Clock, Users, Share2, Copy, Check, ChevronRight, CalendarCheck, Handshake, Coffee, UserPlus, X, Lock } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { fetchAllProfiles } from "@/lib/api";
import { getWeekKey, getMonthKey } from "@/lib/chartUtils";

import axios from "axios";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CopyBtn({ text, dark }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }} className={`p-1.5 rounded-md flex-shrink-0 ${dark ? 'hover:bg-white/20' : 'hover:bg-[#0B2A47]/10'}`}>
      {copied ? <Check className={`w-4 h-4 ${dark ? 'text-[#8FC4E8]' : 'text-[#1E7BC4]'}`} /> : <Copy className={`w-4 h-4 ${dark ? 'text-white/70' : 'text-[#0B2A47]/50'}`} />}
    </button>
  );
}

const EXPECTATIONS = [
  { icon: CalendarCheck, title: "Attend Weekly Meetings", desc: "Consistency builds trust. When you show up every week, your fellow members know they can count on you.", why: "Because people refer those they see and trust regularly." },
  { icon: Handshake, title: "Give Quality Referrals", desc: "Look for real opportunities to connect your fellow members with people who need their services.", why: "Because the best way to get referrals is to give them first." },
  { icon: Coffee, title: "Meet With Members Individually", desc: "Sit down with other members outside the meeting to learn their business inside and out.", why: "Because you can't refer someone you don't truly understand." },
  { icon: UserPlus, title: "Bring Guests", desc: "Invite business professionals who would benefit from and contribute to the group.", why: "Because fresh faces bring fresh opportunities for everyone." },
];

function getNextThursdays() {
  const dates = [];
  const now = new Date();
  const d = new Date(now);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  // Check if today is Thursday
  const isThursday = now.getDay() === 4;

  // If today is Thursday and before end of meeting (say 2 PM), include today
  if (isThursday && now.getHours() < 14) {
    dates.push(new Date(today));
  }

  // Find next Thursday (or next week's Thursday if today is Thu)
  const next = new Date(now);
  if (isThursday) {
    next.setDate(next.getDate() + 7);
  } else {
    while (next.getDay() !== 4) next.setDate(next.getDate() + 1);
  }
  next.setHours(0, 0, 0, 0);

  for (let i = 0; i < 5; i++) {
    dates.push(new Date(next));
    next.setDate(next.getDate() + 7);
  }
  return dates;
}

function VisitorModal({ onClose, members }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [business, setBusiness] = useState("");
  const [visitDate, setVisitDate] = useState("");
  const [invitedBy, setInvitedBy] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const thursdays = getNextThursdays();

  const inviteSources = [...(members || []).map(m => m.name).sort(), "Facebook", "Website", "Google", "Friend", "Other"];

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name || !visitDate) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/visitors/rsvp`, { name, phone, email, business, visitDate });
      const now = new Date();
      const wk = getWeekKey(now);
      const mk = getMonthKey(now);
      // Auto-detect: if visit date is today, mark as "visited", otherwise "scheduled"
      const todayStr = now.toISOString().slice(0, 10);
      const status = visitDate === todayStr ? "visited" : "scheduled";
      await axios.post(`${API}/events`, {
        type: "guest",
        bringer: invitedBy || "Website",
        guestName: name,
        guestPhone: phone,
        guestEmail: email,
        guestBiz: business,
        visitStatus: status,
        visitDate: visitDate,
        weekKey: wk,
        monthKey: mk,
        ts: now.toISOString(),
      });
      setSubmitted(true);
    } catch { /* */ }
    finally { setSubmitting(false); }
  }

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center" onClick={e => e.stopPropagation()}>
          <div className="w-14 h-14 rounded-full bg-[#1E7BC4]/10 flex items-center justify-center mx-auto mb-4">
            <Check className="w-7 h-7 text-[#1E7BC4]" />
          </div>
          <h3 className="text-lg font-bold text-[#0B2A47] mb-1">We'll See You There!</h3>
          <p className="text-sm text-[#5A7080] mb-4">Thanks for signing up. We look forward to meeting you!</p>
          <button onClick={onClose} className="px-6 py-2.5 text-sm font-bold text-white bg-[#0B2A47] rounded-xl">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#0B2A47]">Visit CHART</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#5A7080]" /></button>
        </div>
        <p className="text-xs text-[#5A7080] mb-4">We meet every Thursday at noon. Pick a date and we'll save you a seat!</p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">Your Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} required className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47]" placeholder="Full name" />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">Phone</label>
            <input value={phone} onChange={e => setPhone(e.target.value)} className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47]" placeholder="(843) 555-0000" />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">Email</label>
            <input value={email} onChange={e => setEmail(e.target.value)} type="email" className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47]" placeholder="you@email.com" />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">Business Name</label>
            <input value={business} onChange={e => setBusiness(e.target.value)} className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47]" placeholder="Your company" />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">How did you hear about us? *</label>
            <select value={invitedBy} onChange={e => setInvitedBy(e.target.value)} required className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47]">
              <option value="">Select one</option>
              <optgroup label="Invited by a Member">
                {(members || []).map(m => m.name).sort().map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </optgroup>
              <optgroup label="Found Us Through">
                <option value="Facebook">Facebook</option>
                <option value="Website">Website</option>
                <option value="Google">Google</option>
                <option value="Friend">A Friend</option>
                <option value="Other">Other</option>
              </optgroup>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">Pick a Thursday *</label>
            <select value={visitDate} onChange={e => setVisitDate(e.target.value)} required className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47]">
              <option value="">Select a date</option>
              {thursdays.map((d, i) => {
                const dateStr = d.toISOString().slice(0, 10);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const isToday = d.getTime() === today.getTime();
                return (
                  <option key={dateStr} value={dateStr}>
                    {isToday ? "Today — " : ""}{d.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}
                    {isToday ? " (I'm here now!)" : ""}
                  </option>
                );
              })}
            </select>
          </div>
          <button type="submit" disabled={submitting} className="w-full py-3 text-sm font-bold text-white bg-[#1E7BC4] hover:bg-[#1769A8] rounded-xl disabled:opacity-50 transition-colors">
            {submitting ? "Submitting..." : "Reserve My Seat"}
          </button>
        </form>
      </div>
    </div>
  );
}

function ApplicationModal({ onClose, members }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [business, setBusiness] = useState("");
  const [industry, setIndustry] = useState("");
  const [referredBy, setReferredBy] = useState("");
  const [message, setMessage] = useState("");
  const [agreedToRules, setAgreedToRules] = useState(false);
  const [signatureName, setSignatureName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const today = new Date().toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" });

  const EXPECTATIONS = [
    "Give quality referrals to fellow members. We grow by helping each other.",
    "Give more than you take. The best way to receive is to be the first to give.",
    "Schedule and complete 1-on-1 meetings with other members to build real relationships.",
    "Bring qualified guests who could benefit from and contribute to the group.",
    "Be professional, on time, and prepared. Respect everyone's time and business.",
    "Support and promote fellow members whenever the opportunity arises.",
  ];

  const ATTENDANCE_BULLETS = [
    "Each member is allowed 3 absences per quarter. This includes client or job meetings that could not be moved.",
    "Family and medical situations do not count against your 3. This includes a death in the family, a medical emergency, or a sick child or grandchild. No explanation is required.",
    "Any time you know you will miss, message the group as soon as you know and make every attempt to send a substitute. A substitute can be an employee, a business partner, a spouse, or a colleague who can hold your seat and take notes. Sending a substitute does not count against your 3 absences.",
    "Members are expected to stay for the full meeting. Attending for only 5 to 10 minutes does not count as attendance.",
    "Attendance is tracked. If you are approaching or reach 3 absences in a quarter, leadership will reach out to you privately.",
    "Exceeding 3 absences in a quarter will prompt a check-in from leadership. A repeated pattern across quarters may result in your seat being opened to another applicant.",
  ];

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name || !email || !agreedToRules || !signatureName.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, { name, phone, email, business, industry, heardAbout: referredBy, message });
      setSubmitted(true);
    } catch { /* */ }
    finally { setSubmitting(false); }
  }

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center" onClick={e => e.stopPropagation()}>
          <div className="w-14 h-14 rounded-full bg-[#1E7BC4]/10 flex items-center justify-center mx-auto mb-4">
            <Check className="w-7 h-7 text-[#1E7BC4]" />
          </div>
          <h3 className="text-lg font-bold text-[#0B2A47] mb-1">Application Received!</h3>
          <p className="text-sm text-[#5A7080] mb-4">Thanks for your interest in CHART. A member will be in touch with you soon.</p>
          <button onClick={onClose} className="px-6 py-2.5 text-sm font-bold text-white bg-[#0B2A47] rounded-xl">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#0B2A47]">Apply to Join CHART</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#5A7080]" /></button>
        </div>

        {/* Membership Expectations */}
        <div className="bg-[#0B2A47] rounded-xl p-4 mb-3">
          <h4 className="text-sm font-extrabold text-white mb-2">Membership Expectations</h4>
          <p className="text-[11px] text-[#8FC4E8] mb-3">By applying, you agree to uphold these commitments:</p>

          {/* Attendance Policy — visually distinct */}
          <div className="bg-[#1E7BC4]/20 border border-[#1E7BC4]/40 rounded-lg p-3 mb-3">
            <h5 className="text-[11px] font-extrabold text-[#8FC4E8] tracking-widest uppercase mb-1.5">1. Attendance Policy</h5>
            <p className="text-[11px] text-white/90 leading-relaxed mb-2 font-semibold">
              Attend the weekly Thursday meeting consistently. Members are expected at every meeting.
            </p>
            <ul className="space-y-1.5">
              {ATTENDANCE_BULLETS.map((bullet, i) => (
                <li key={i} className="flex gap-1.5 text-[10px] text-white/80 leading-relaxed">
                  <span className="text-[#8FC4E8] mt-0.5 flex-shrink-0">&#8226;</span>
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Remaining expectations */}
          <ol start={2} className="space-y-2">
            {EXPECTATIONS.map((rule, i) => (
              <li key={i} className="flex gap-2 text-[11px] text-white/85 leading-relaxed">
                <span className="text-[#1E7BC4] font-extrabold flex-shrink-0">{i + 2}.</span>
                {rule}
              </li>
            ))}
          </ol>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">Your Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} required className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47]" placeholder="Full name" />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">Email *</label>
            <input value={email} onChange={e => setEmail(e.target.value)} type="email" required className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47]" placeholder="you@email.com" />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">Phone</label>
            <input value={phone} onChange={e => setPhone(e.target.value)} className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47]" placeholder="(843) 555-0000" />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">Business Name</label>
            <input value={business} onChange={e => setBusiness(e.target.value)} className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47]" placeholder="Your company" />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">Industry</label>
            <input value={industry} onChange={e => setIndustry(e.target.value)} className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47]" placeholder="e.g. Real Estate, Insurance, Marketing" />
          </div>
          <div>
            <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">Who referred you / How did you find us? *</label>
            <select value={referredBy} onChange={e => setReferredBy(e.target.value)} required className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47]">
              <option value="">Select one</option>
              <optgroup label="Referred by a Member">
                {(members || []).map(m => m.name).sort().map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </optgroup>
              <optgroup label="Found Us Through">
                <option value="Facebook">Facebook</option>
                <option value="Google">Google</option>
                <option value="Website">This Website</option>
                <option value="Friend">A Friend</option>
                <option value="Other">Other</option>
              </optgroup>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">Why do you want to join?</label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3} className="w-full px-3 py-2.5 text-sm border border-[#D5D0C8] rounded-lg outline-none focus:border-[#1E7BC4] text-[#0B2A47] resize-none" placeholder="Tell us a bit about yourself..." />
          </div>

          {/* Acknowledgment + Signature */}
          <div className="border-t border-[#E5E0D8] pt-3 mt-1">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={agreedToRules} onChange={e => setAgreedToRules(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-[#D5D0C8] text-[#1E7BC4] focus:ring-[#1E7BC4] cursor-pointer" />
              <span className="text-xs text-[#0B2A47] leading-relaxed font-semibold">I have read and agree to the CHART membership expectations and attendance policy above.</span>
            </label>
            <div className="flex items-end gap-3 mt-3">
              <div className="flex-1">
                <label className="block text-[10px] font-bold text-[#5A7080] tracking-widest uppercase mb-1">Signature (type full name) *</label>
                <input
                  value={signatureName}
                  onChange={e => setSignatureName(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 text-sm border-b-2 border-[#0B2A47] bg-transparent outline-none text-[#0B2A47] italic font-semibold placeholder:not-italic placeholder:font-normal placeholder:text-[#C5D0DA]"
                  placeholder="Your full name"
                />
              </div>
              <div className="flex-shrink-0 pb-2.5">
                <span className="text-xs text-[#5A7080] font-semibold">{today}</span>
              </div>
            </div>
          </div>

          <button type="submit" disabled={submitting || !agreedToRules || !signatureName.trim()} className="w-full py-3 text-sm font-bold text-white bg-[#0B2A47] hover:bg-[#0D3358] rounded-xl disabled:opacity-40 transition-colors">
            {submitting ? "Submitting..." : "Submit Application"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVisitorForm, setShowVisitorForm] = useState(false);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedDir, setCopiedDir] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const profiles = await fetchAllProfiles();
        setMembers(profiles.sort((a, b) => a.name.localeCompare(b.name)));
      } catch {
        setMembers([]);
      } finally {
        setLoading(false);
      }
    }
    load();
    document.title = "CHART Networking | Charleston Area Referral Team";
  }, []);

  const siteUrl = window.location.origin;
  const directoryUrl = `${siteUrl}/members`;
  const colors = [
    "bg-[#1E7BC4]", "bg-[#1E7BC4]", "bg-[#1E7BC4]", "bg-[#1E7BC4]",
    "bg-[#1E7BC4]", "bg-[#1E7BC4]", "bg-[#1E7BC4]", "bg-[#1E7BC4]", "bg-[#1E7BC4]"
  ];

  function handleShare() {
    const url = siteUrl;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      window.prompt("Copy this link:", url);
    });
  }

  function handleShareDirectory() {
    const url = directoryUrl;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedDir(true);
      setTimeout(() => setCopiedDir(false), 2000);
    }).catch(() => {
      window.prompt("Copy this link:", url);
    });
  }

  return (
    <div className="min-h-screen bg-[#F5F2EC]">

      {/* ═══════ STICKY HEADER ═══════ */}
      <div className="bg-[#1E7BC4] text-white sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-2.5 flex items-center justify-between">
          <button onClick={handleShare} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-[#1E7BC4] bg-white hover:bg-[#F5F2EC] rounded-lg transition-colors">
            {copied ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Share2 className="w-3.5 h-3.5" /> Share</>}
          </button>
          <div className="flex items-center gap-2">
            <Link to="/members" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-white/20 hover:bg-white/30 rounded-lg transition-colors">
              <Users className="w-3.5 h-3.5" /> Members
            </Link>
            <Link to="/tracker" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-white/20 hover:bg-white/30 rounded-lg transition-colors">
              <Lock className="w-3.5 h-3.5" /> Login
            </Link>
          </div>
        </div>
      </div>

      {/* ═══════ HERO ═══════ */}
      <div className="bg-[#0B2A47] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(30,123,196,0.15),transparent_60%)]" />
        <div className="max-w-3xl mx-auto px-5 pt-10 pb-4 text-center relative z-10">
          <div className="relative mx-auto mb-4 w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center">
            <div className="absolute inset-0 bg-[#1E7BC4]/25 rounded-full blur-xl" />
            <img src="/icon-dark.png" alt="" className="h-20 sm:h-24 object-contain relative z-10" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-1">CHART</h1>
          <p className="text-[#8FC4E8] text-sm tracking-widest uppercase">Charleston Area Referral Team</p>
        </div>
        <div className="max-w-xs mx-auto border-t border-white/10 my-2" />
        <div className="max-w-3xl mx-auto px-5 pb-10 text-center relative z-10">
          <p className="text-lg sm:text-xl font-semibold text-white mb-3 italic leading-snug">
            "Relationships are what truly create referrals."
          </p>
          <p className="text-[#8FC4E8] text-sm max-w-md mx-auto mb-8">
            For 15 years, we've been a group of Charleston area business owners who meet every week to build real relationships, exchange quality referrals, and help each other grow.
          </p>

          <div className="inline-flex flex-col sm:flex-row items-center gap-3 sm:gap-6 text-sm">
            <span className="inline-flex items-center gap-2 text-[#C5D5E5]"><Clock className="w-4 h-4 text-[#1E7BC4]" /> Every Thursday 12–1:30</span>
            <span className="inline-flex items-center gap-2 text-[#C5D5E5]"><MapPin className="w-4 h-4 text-[#1E7BC4]" /> Bahama Breeze, North Charleston, SC</span>
          </div>
        </div>
      </div>

      {/* ═══════ VISITORS WELCOME ═══════ */}
      <div className="bg-[#1E7BC4] text-white text-center py-10 px-6">
        <p className="text-2xl sm:text-3xl font-extrabold mb-2 tracking-tight">Visitors Are Always Welcome!</p>
        <p className="text-white/80 text-sm sm:text-base mb-5 max-w-md mx-auto">Come see what CHART is all about. Pick a Thursday and we'll save you a seat.</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={() => setShowVisitorForm(true)}
            className="inline-flex items-center gap-2 px-8 py-4 text-base font-extrabold text-[#1E7BC4] bg-white hover:bg-[#F5F2EC] rounded-xl transition-colors shadow-lg shadow-black/10"
          >
            <CalendarCheck className="w-5 h-5" /> RSVP to Visit
          </button>
          <button
            onClick={() => setShowApplyForm(true)}
            className="inline-flex items-center gap-2 px-8 py-4 text-base font-extrabold text-white bg-[#0B2A47] hover:bg-[#0D3358] rounded-xl transition-colors shadow-lg shadow-black/10"
          >
            <UserPlus className="w-5 h-5" /> Apply to Join
          </button>
        </div>
      </div>

      {showVisitorForm && <VisitorModal onClose={() => setShowVisitorForm(false)} members={members} />}
      {showApplyForm && <ApplicationModal onClose={() => setShowApplyForm(false)} members={members} />}

      {/* ═══════ WHAT WE'RE ABOUT ═══════ */}
      <div id="about" className="max-w-3xl mx-auto px-5 py-12">
        <h2 className="font-heading text-2xl font-extrabold text-[#0B2A47] text-center mb-2">What We're About</h2>
        <p className="text-[#5A7080] text-sm text-center mb-8 max-w-md mx-auto">
          CHART isn't just a meeting. It's a commitment to supporting each other's businesses through genuine relationships.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {EXPECTATIONS.map((exp, i) => (
            <div key={i} className="bg-white rounded-xl border border-[#E5E0D8] p-5 hover:border-[#1E7BC4]/40 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-[#0B2A47]/5 flex items-center justify-center mb-3">
                <exp.icon className="w-5 h-5 text-[#1E7BC4]" />
              </div>
              <h3 className="text-sm font-bold text-[#0B2A47] mb-1">{exp.title}</h3>
              <p className="text-xs text-[#5A7080] leading-relaxed mb-2">{exp.desc}</p>
              <p className="text-xs font-semibold text-[#1E7BC4] italic">{exp.why}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ═══════ WHO WE ARE ═══════ */}
      <div className="bg-[#1E7BC4] border-y border-[#1769A8]">
        <div className="max-w-3xl mx-auto px-5 py-12 text-center">
          <img src="/icon-dark.png" alt="" className="h-12 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-extrabold text-white mb-4">Who We Are</h2>
          <p className="text-white/85 text-sm leading-relaxed max-w-xl mx-auto mb-3">
            CHART, the <strong>Charleston Area Referral Team</strong>, has been connecting local business owners since 2011. What started as a small group of professionals looking to grow through word of mouth has become one of the most trusted networking groups in the Charleston area.
          </p>
          <p className="text-white/85 text-sm leading-relaxed max-w-xl mx-auto mb-3">
            We believe the best business comes from real relationships, not cold calls or ads. When you know someone, trust them, and understand their work, you refer them with confidence. That's the CHART way.
          </p>
          <p className="text-white/85 text-sm leading-relaxed max-w-xl mx-auto">
            Our members represent a wide range of industries, from insurance and real estate to pressure washing and financial planning. Together, we are each other's sales force.
          </p>
        </div>
      </div>

      {/* ═══════ MEET OUR MEMBERS CTA ═══════ */}
      <div className="max-w-3xl mx-auto px-5 py-12">
        <div className="bg-white border border-[#E5E0D8] rounded-2xl p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-[#1E7BC4]/10 flex items-center justify-center mx-auto mb-4">
            <Users className="w-7 h-7 text-[#1E7BC4]" />
          </div>
          <h2 className="font-heading text-2xl font-extrabold text-[#0B2A47] mb-2">Meet Our Members</h2>
          <p className="text-[#5A7080] text-sm mb-6 max-w-sm mx-auto">
            See who's in our group, what they do, and how to reach them.
          </p>
          <Link
            to="/members"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold text-white bg-[#1E7BC4] hover:bg-[#1769A8] rounded-xl transition-colors"
          >
            <Users className="w-4 h-4" /> View Member Directory
          </Link>
        </div>
      </div>

      {/* ═══════ SHARE / QR FOOTER ═══════ */}
      <div className="bg-[#0B2A47] text-white">
        <div className="max-w-3xl mx-auto px-5 py-12 text-center">
          <img src="/icon-dark.png" alt="CHART" className="h-14 mx-auto mb-6" />
          <h3 className="font-heading text-xl font-extrabold mb-1">Share CHART Networking</h3>
          <p className="text-[#6888A5] text-sm mb-6">Know someone who should join? Send them our directory.</p>

          <div className="inline-block bg-white p-4 rounded-xl mb-5">
            <QRCodeSVG value={directoryUrl} size={160} level="M" bgColor="#ffffff" fgColor="#0B2A47" />
          </div>

          <div className="flex items-center gap-2 max-w-sm mx-auto bg-white/10 border border-white/20 rounded-lg px-3 py-2.5 mb-6">
            <Globe className="w-4 h-4 text-white/50 flex-shrink-0" />
            <a href="/members" className="text-sm font-mono text-white/80 truncate flex-1 hover:text-white underline underline-offset-2">{directoryUrl}</a>
            <CopyBtn text={directoryUrl} dark />
          </div>

          <button
            onClick={handleShareDirectory}
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold text-white bg-[#1E7BC4] hover:bg-[#1769A8] rounded-xl transition-colors"
          >
            {copiedDir ? <><Check className="w-4 h-4" /> Link Copied!</> : <><Share2 className="w-4 h-4" /> Share Directory</>}
          </button>

          <div className="mt-8">
            <Link to="/tracker" className="inline-flex items-center gap-1.5 text-xs text-[#4A6A85] hover:text-white transition-colors">
              <Lock className="w-3 h-3" /> Members Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
